import { MONETARY_DECIMAL_PLACES } from "@facturar/money-format";
/**
 * /settings — App + account settings (NOT profile data).
 *
 * Sections:
 *   - Locale + timezone   — UI preferences
 *   - Account info        — read-only phone + role
 *   - Logout
 *
 * Personal info (display name, identity, email) lives in /profile.
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { KeyRound, Loader2, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProfile, useUpdateProfile } from "@/lib/api/profile.queries";

// ── Allowed values (closed sets — UI never lets the user type free text) ─────

const SUPPORTED_LOCALES = [
  { value: "es-CO", labelKey: "settings.locale.option_es_co" },
  { value: "en-US", labelKey: "settings.locale.option_en_us" },
] as const;

const SUPPORTED_TIMEZONES = [
  { value: "America/Bogota",      labelKey: "settings.timezone.option_bogota" },
  { value: "America/Mexico_City", labelKey: "settings.timezone.option_mexico_city" },
  { value: "America/Lima",        labelKey: "settings.timezone.option_lima" },
  { value: "America/Caracas",     labelKey: "settings.timezone.option_caracas" },
  { value: "America/Panama",      labelKey: "settings.timezone.option_panama" },
  { value: "America/New_York",    labelKey: "settings.timezone.option_new_york" },
  { value: "Europe/Madrid",       labelKey: "settings.timezone.option_madrid" },
  { value: "UTC",                 labelKey: "settings.timezone.option_utc" },
] as const;

export const Route = createFileRoute("/settings/")({
  component: SettingsPage,
});

function SettingsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SettingsContent />
      </AppShell>
    </RequireAuth>
  );
}

function SettingsContent() {
  const { t } = useTranslation();
  const profileQuery = useProfile();

  if (profileQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  const profile = profileQuery.data?.profile;
  const user = profileQuery.data?.user;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("settings.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings.subtitle")}
        </p>
      </div>

      {/* Locale + timezone */}
      <LocaleSection
        locale={profile?.locale ?? "es-CO"}
        timezone={profile?.timezone ?? "America/Bogota"}
        monetaryDecimalPlaces={profile?.monetaryDecimalPlaces ?? 2}
      />

      {/* Account info (read-only) */}
      {user && (
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.account.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-1">
              <Label className="text-xs text-muted-foreground">
                {t("settings.account.phone")}
              </Label>
              <p className="font-mono text-sm">{user.phone}</p>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs text-muted-foreground">
                {t("settings.account.role")}
              </Label>
              <p className="text-sm capitalize">{user.role}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personal access tokens (API) */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.tokens.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("settings.tokens.body")}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/tokens">
              <KeyRound className="mr-2 h-4 w-4" />
              {t("settings.tokens.cta")}
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Logout */}
      <LogoutSection />
    </div>
  );
}

// ── Locale section ────────────────────────────────────────────────────────────

function LocaleSection({
  locale,
  timezone,
  monetaryDecimalPlaces,
}: {
  locale: string;
  timezone: string;
  monetaryDecimalPlaces: number;
}) {
  const { t, i18n } = useTranslation();

  // Snap incoming values to the supported set; defaults preserve familiar
  // Pah Venture defaults if a stale value sneaks in from the DB.
  const initialLoc = SUPPORTED_LOCALES.some((l) => l.value === locale)
    ? locale
    : "es-CO";
  const initialTz = SUPPORTED_TIMEZONES.some((z) => z.value === timezone)
    ? timezone
    : "America/Bogota";

  const [loc, setLoc] = useState(initialLoc);
  const [tz, setTz] = useState(initialTz);
  const [digits, setDigits] = useState(monetaryDecimalPlaces);
  const updateProfile = useUpdateProfile();

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      await updateProfile.mutateAsync({ locale: loc, timezone: tz, monetaryDecimalPlaces: digits });
      toast.success(t("settings.locale.saved"));
      // Switch the live UI language. i18next supports the bare code (es/en);
      // strip the region tag.
      const bare = loc.split("-")[0]!;
      if (bare !== i18n.language) {
        await i18n.changeLanguage(bare);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("profile.err_generic")
      );
    }
  }

  const dirty = loc !== initialLoc || tz !== initialTz || digits !== monetaryDecimalPlaces;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.locale.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="s-locale">
              {t("settings.locale.locale_label")}
            </Label>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger id="s-locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LOCALES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="s-tz">{t("settings.locale.timezone_label")}</Label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger id="s-tz">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_TIMEZONES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="s-money-digits">{t("settings.locale.monetary_decimal_places")}</Label>
            <Select value={String(digits)} onValueChange={(value) => setDigits(Number(value))}>
              <SelectTrigger id="s-money-digits"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONETARY_DECIMAL_PLACES.map((value) => <SelectItem key={value} value={String(value)}>{value}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t("settings.locale.monetary_decimal_places_help")}</p>
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={updateProfile.isPending || !dirty}
          >
            {updateProfile.isPending && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            {t("settings.locale.save_cta")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Logout section ────────────────────────────────────────────────────────────

function LogoutSection() {
  const { t } = useTranslation();

  function handleLogout() {
    window.location.href = "/logout";
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <Button
          variant="outline"
          onClick={handleLogout}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t("app.logout")}
        </Button>
      </CardContent>
    </Card>
  );
}
